import { useCallback } from 'react';

import { mainStore } from '~/stores';

/**
 * Haptic feedback types available in React Native
 *
 * Platform availability:
 * - ✓ = Works on both iOS and Android
 * - iOS only = Only works on iOS
 * - Android only = Only works on Android
 */
export type HapticFeedbackType =
  | 'selection' // ⚠️ iOS only (NOT available on Android)
  | 'impactLight' // ✓ Cross-platform
  | 'impactMedium' // ✓ Cross-platform
  | 'impactHeavy' // ✓ Cross-platform
  | 'rigid' // ✓ Cross-platform
  | 'soft' // ✓ Cross-platform
  | 'notificationSuccess' // ✓ Cross-platform
  | 'notificationWarning' // ✓ Cross-platform
  | 'notificationError' // ✓ Cross-platform
  | 'clockTick' // ⚠️ Android only (NOT available on iOS)
  | 'contextClick' // ⚠️ Android only (NOT available on iOS)
  | 'keyboardPress' // ⚠️ Android only (NOT available on iOS)
  | 'keyboardRelease' // ⚠️ Android only (NOT available on iOS)
  | 'keyboardTap' // ⚠️ Android only (NOT available on iOS)
  | 'longPress' // ⚠️ Android only (NOT available on iOS)
  | 'textHandleMove' // ⚠️ Android only (NOT available on iOS)
  | 'virtualKey' // ⚠️ Android only (NOT available on iOS)
  | 'virtualKeyRelease' // ⚠️ Android only (NOT available on iOS)
  | 'effectClick' // ⚠️ Android only (NOT available on iOS)
  | 'effectDoubleClick' // ⚠️ Android only (NOT available on iOS)
  | 'effectHeavyClick' // ⚠️ Android only (NOT available on iOS)
  | 'effectTick'; // ⚠️ Android only (NOT available on iOS)

/**
 * Options for vibration
 */
interface VibrationOptions {
  /**
   * Haptic feedback type from React Native
   *
   * ⚠️ IMPORTANT: Use cross-platform types (marked with ✓) for best compatibility:
   * - 'soft' - soft tactile feedback (recommended for selections)
   * - 'impactLight' - light physical impact
   * - 'impactMedium' - medium physical impact
   * - 'impactHeavy' - heavy physical impact
   * - 'notificationSuccess' - success notification feedback
   * - 'notificationWarning' - warning notification feedback
   * - 'notificationError' - error notification feedback
   * - 'rigid' - rigid feedback
   */
  type?: HapticFeedbackType;

  /**
   * Whether to check if feature is enabled (for specific features)
   * Default: false (always vibrate if in RN)
   */
  checkFeatureFlag?: boolean;

  /**
   * Feature flag checker function
   */
  isFeatureEnabled?: () => boolean;
}

/**
 * Hook return type
 */
interface UseVibrationReturn {
  /**
   * Trigger vibration with specified type
   * @param type - Haptic feedback type
   */
  vibrate: (type?: HapticFeedbackType) => void;

  /**
   * Check if vibration is available (running in RN WebView)
   */
  isAvailable: boolean;
}

/**
 * Universal hook for haptic feedback in React Native WebView
 *
 * Provides a simple API to trigger vibrations anywhere in the app.
 * Works only in React Native WebView environment.
 *
 * ⚠️ IMPORTANT: Always use cross-platform types (marked with ✓) for best compatibility.
 *
 * @example
 * ```tsx
 * // Basic usage with cross-platform type
 * const { vibrate, isAvailable } = useVibration();
 *
 * const handleClick = () => {
 *   vibrate('soft'); // Soft tactile feedback (cross-platform)
 * };
 *
 * // With default type
 * const { vibrate } = useVibration({ type: 'impactMedium' });
 * vibrate(); // Medium impact vibration (cross-platform)
 *
 * // With feature flag check
 * const { vibrate } = useVibration({
 *   type: 'notificationSuccess',
 *   checkFeatureFlag: true,
 *   isFeatureEnabled: () => company.config.enableSomeFeature,
 * });
 * ```
 *
 * @param options - Hook options
 * @returns Object with vibrate function and availability flag
 */
export const useVibration = (
  options: VibrationOptions = {},
): UseVibrationReturn => {
  const {
    type: defaultType = 'soft',
    checkFeatureFlag = false,
    isFeatureEnabled,
  } = options;

  const isAvailable = mainStore.isRN;

  const vibrate = useCallback(
    (type: HapticFeedbackType = defaultType) => {
      if (!mainStore.isRN) {
        return;
      }

      if (checkFeatureFlag && isFeatureEnabled && !isFeatureEnabled()) {
        return;
      }

      mainStore.sendToRN('hapticFeedback', {
        count: 1,
        type,
      });
    },
    [defaultType, checkFeatureFlag, isFeatureEnabled],
  );

  return {
    vibrate,
    isAvailable,
  };
};
